import type { FC, ReactNode } from 'react';
import { useMemo } from 'react';
import styled from 'threads5/styles/styled';
import type { ChangelogProductId } from 'src/components/docs/Changelog/products';
import { useChangelogFilter } from 'src/components/docs/Changelog/ChangelogFilterContext';

import styles from './ChangelogEntry.module.scss';

const StyledEntry = styled('article', {
  shouldForwardProp: (prop) => {
    return prop !== 'isVisible';
  },
})<{ isVisible: boolean }>(({ isVisible }) => {
  return {
    display: isVisible ? 'block' : 'none',
  };
});

const StyledDateHeading = styled('h5')(({ theme }) => {
  return {
    fontFamily: "'Plaid Sans', Helvetica, Arial, sans-serif",
    marginBottom: '16px',
    position: 'relative',
    [theme.breakpoints.up('md')]: {
      marginBottom: '16px',
    },
  };
});

const StyledHeadingLink = styled('a')({
  color: 'inherit',
  textDecoration: 'none',
  cursor: 'pointer',
});

const slugify = (text: string): string => {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
};

/**
 * Shortcode that wraps a single changelog entry. Filtering toggles
 * `display: none`; the entry's slugified date is exposed as an anchor id
 * so external `#may-20-2026`-style links keep resolving when the entry is
 * visible under the active filter.
 *
 * This markup is scraped by the changelog RSS feed linked from changelog.mdx,
 * which depends on three selectors here:
 *
 * - `article[data-products]` delimits one feed item
 * - `h5 a` supplies the item title
 * - `div.content` supplies the item body
 */
const ChangelogEntry: FC<{
  date: string;
  products: ReadonlyArray<ChangelogProductId>;
  children: ReactNode;
}> = ({ date, products, children }) => {
  const { activeProducts } = useChangelogFilter();
  const slug = useMemo<string>(() => {
    return slugify(date);
  }, [date]);

  const isVisible = useMemo<boolean>(() => {
    if (activeProducts.size === 0) return true;
    for (const id of products) {
      if (activeProducts.has(id)) return true;
    }
    return false;
  }, [activeProducts, products]);

  return (
    <StyledEntry isVisible={isVisible} data-products={products.join(',')}>
      <StyledDateHeading>
        <StyledHeadingLink href={`#${slug}`}>
          {date}
          <span
            className={styles.offsetAnchor}
            id={slug}
            data-algolia='linkedHeadingAnchor'
          />
        </StyledHeadingLink>
      </StyledDateHeading>
      <div className='content'>{children}</div>
    </StyledEntry>
  );
};

export default ChangelogEntry;
